import {ErrorHandler, Injectable, Injector} from '@angular/core';
import {Router} from '@angular/router';
import {HttpErrorResponse} from '@angular/common/http';
import * as Sentry from '@sentry/angular';

import packageJson from '@packageJson';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

  private readonly appVersion: string = packageJson.version;

  constructor(
    private readonly injector: Injector
  ) {
  }


  /**
   * Manejar el error
   *
   */
  handleError(error: any) {
    const errorContext = {
      timestamp: new Date().toISOString(),
      url: this.injector.get(Router)?.url || window.location.href,
      userAgent: navigator.userAgent,
      errorName: error?.constructor?.name || 'Unknown',
      errorType: this.getErrorType(error),
      errorMessage: this.getErrorMessage(error),
      appVersion: this.appVersion,
    };

    Sentry.withScope((scope) => {
      scope.setTag('capturedBy', 'GlobalErrorHandler');
      scope.setTag('errorType', errorContext.errorType);
      scope.setTag('appVersion', errorContext.appVersion);
      scope.setContext('application', errorContext);
      scope.setLevel('error');

      this.setSpecificErrorContext(scope, error);

      scope.setFingerprint([
        errorContext.errorType,
        errorContext.errorName,

        this.getErrorLocation(error),
      ]);

      Sentry.captureException(error);
    });

    console.error(error);
  }


  /**
   * Obtener el tipo de error
   *
   */
  private getErrorType(error: any): string {

    if (error instanceof HttpErrorResponse) {
      return 'HTTP';
    }

    if (error instanceof TypeError) {
      return 'TypeError';
    }

    if (error instanceof ReferenceError) {
      return 'ReferenceError';
    }

    if (error instanceof RangeError) {
      return 'RangeError';
    }

    if (error?.rejection) {
      return 'PromiseRejection';
    }

    if (error?.promise) {
      return 'UnhandledPromise';
    }

    return 'Generic';
  }


  /**
   * Obtener el mensaje del error
   *
   */
  private getErrorMessage(error: any): string {

    if (error?.message) {
      return error.message;
    }

    if (error?.error?.message) {
      return error.error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'No error message available';
  }


  /**
   * Obtener el lugar del error
   *
   */
  private getErrorLocation(error: any): string {

    if (!error?.stack) {
      return 'unknown';
    }

    const stackLines = error.stack.split('\n');
    const relevantLine = stackLines.find((line: string) =>
      line.includes('.ts:') || line.includes('.js:')
    );

    return relevantLine ? relevantLine.split(' ')[0] : 'unknown';
  }


  /**
   * Establecer el contexto del error
   *
   */
  private setSpecificErrorContext(scope: any, error: any) {

    if (error instanceof HttpErrorResponse) {
      scope.setContext('httpError', {
        status: error.status,
        statusText: error.statusText,
        url: error.url,
        method: 'Unknown',
      });

      scope.setTag('httpStatus', error.status.toString());
    }

    if (error?.stack) {
      scope.setContext('stackTrace', {
        stack: error.stack,
        fileName: error.fileName,
        lineNumber: error.lineNumber,
        columnNumber: error.columnNumber,
      });
    }

    if (error?.rejection) {
      scope.setContext('promiseRejection', {
        rejection: error.rejection,
        rejectionType: typeof error.rejection,
      });
    }

    if ((window as any).Zone?.current) {
      scope.setContext('angularZone', {
        zoneName: (window as any).Zone.current.name,
        properties: Object.keys((window as any).Zone.current.properties || {}),
      });
    }
  }

}
